import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomerEditForm from "../customereditform";
import TaxOfficeDetailsDialog from "../../../components/TaxOfficeDetailsDialog";
import {
  customerCardPanelHeaderSx,
  customerCardPanelLabelSx,
  customerCardPanelTitleTypographyProps,
  customerCardPanelValueSx,
} from "./customerCardPanelStyles";

const DetailItem = ({ label, value, action }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "220px minmax(0, 1fr)",
      columnGap: 1,
      alignItems: "start",
    }}
  >
    <Typography variant="body2" sx={customerCardPanelLabelSx}>
      {label}:
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="body2" sx={customerCardPanelValueSx}>
        {value || "-"}
      </Typography>
      {action || null}
    </Box>
  </Box>
);

const CustomerCardSummaryPanel = ({ customerId }) => {
  const [entity, setEntity] = useState(null);
  const [assignedTaxOffice, setAssignedTaxOffice] = useState(null);
  const [countries, setCountries] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isTaxOfficeViewOpen, setIsTaxOfficeViewOpen] = useState(false);
  const [editDialogKey, setEditDialogKey] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleCopyEmail = async () => {
    if (!entity?.generalEmailAddress) {
      return;
    }

    try {
      await navigator.clipboard.writeText(entity.generalEmailAddress);
      setCopyFeedback({
        open: true,
        severity: "success",
        message: "Email copied to clipboard.",
      });
    } catch (error) {
      console.error("Could not copy email to clipboard:", error);
      setCopyFeedback({
        open: true,
        severity: "error",
        message: "Copy to clipboard failed.",
      });
    }
  };

  const fetchEntity = async () => {
    if (!customerId) {
      setEntity(null);
      return;
    }

    const response = await axios.get(`/api/v1/entities/${customerId}`);
    setEntity(response.data);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!customerId) {
        return;
      }

      const [entityResponse, countriesResponse] = await Promise.all([
        axios.get(`/api/v1/entities/${customerId}`),
        axios.get("/api/v1/countries"),
      ]);

      setEntity(entityResponse.data);
      setCountries(countriesResponse.data || []);
    };

    fetchData().catch((error) => console.error("Error loading customer summary:", error));
  }, [customerId]);

  useEffect(() => {
    if (!entity?.taxOfficeId) {
      setAssignedTaxOffice(null);
      return;
    }

    let isCancelled = false;

    axios
      .get(`/api/v1/taxOffices/${entity.taxOfficeId}`)
      .then((response) => {
        if (!isCancelled) {
          setAssignedTaxOffice(response.data);
        }
      })
      .catch((error) => {
        console.error("Error loading tax office:", error);
        if (!isCancelled) {
          setAssignedTaxOffice(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [entity?.taxOfficeId]);

  const countryCode = useMemo(() => {
    if (!entity?.countryId) {
      return "";
    }

    const country = countries.find((item) => item.countryId === entity.countryId);
    return country?.countryCode || "";
  }, [countries, entity]);

  const fullAddress = useMemo(() => {
    if (!entity) {
      return "";
    }

    return [entity.address, entity.houseNumber].filter(Boolean).join(" ").trim();
  }, [entity]);

  const taxOfficeName = assignedTaxOffice?.name || entity?.taxOffice || "";
  const taxOfficeNumber = (assignedTaxOffice?.taxOfficeNumber || entity?.taxOfficeNumber || "").trim();
  const canViewTaxOffice = Boolean(entity?.taxOfficeId || assignedTaxOffice?.taxOfficeId);

  const handleViewTaxOffice = () => {
    if (!canViewTaxOffice) {
      return;
    }

    setIsTaxOfficeViewOpen(true);
  };

  const handleViewTaxOfficeKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleViewTaxOffice();
    }
  };

  const handleEditClose = async () => {
    setIsEditOpen(false);
    setEditDialogKey((current) => current + 1);
    await fetchEntity();
  };

  return (
    <Card>
      <CardHeader
        title="Customer Information"
        titleTypographyProps={customerCardPanelTitleTypographyProps}
        sx={customerCardPanelHeaderSx}
        action={
          <Tooltip title="Edit customer details">
            <IconButton
              size="small"
              aria-label="edit customer details"
              onClick={() => {
                setEditDialogKey((current) => current + 1);
                setIsEditOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <Divider />
      <CardContent sx={{ px: 2, pt: 1, pb: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <DetailItem label="Customer Number" value={entity?.entityNumber} />
          <DetailItem label="Company Name" value={entity?.name} />
          <DetailItem label="Address" value={fullAddress} />
          <DetailItem label="Postcode" value={entity?.postalCode} />
          <DetailItem label="City" value={entity?.city} />
          <DetailItem label="Country" value={countryCode} />
          <DetailItem
            label="General Email"
            value={entity?.generalEmailAddress}
            action={
              entity?.generalEmailAddress ? (
                <Tooltip title="Copy email">
                  <IconButton size="small" aria-label="copy general email" onClick={handleCopyEmail}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          />
          <DetailItem label="General Phone Number" value={entity?.generalPhoneNumber} />
          <DetailItem label="VAT Number" value={entity?.vat} />
          {countryCode === "DE" || entity?.steuerNummer ? (
            <DetailItem label="Steuernummer" value={entity?.steuerNummer} />
          ) : null}
          <DetailItem label="CoC Number" value={entity?.coC} />
          <DetailItem
            label="Tax Office"
            value={taxOfficeName}
            action={
              canViewTaxOffice ? (
                <Tooltip title="View tax office">
                  <IconButton
                    size="small"
                    aria-label="view tax office"
                    tabIndex={0}
                    onClick={handleViewTaxOffice}
                    onKeyDown={handleViewTaxOfficeKeyDown}
                  >
                    <VisibilityIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          />
          {taxOfficeNumber ? <DetailItem label="Tax Office Number" value={taxOfficeNumber} /> : null}
          <DetailItem
            label="Start Date"
            value={entity?.startupDate ? moment(entity.startupDate).format("DD-MM-YYYY") : ""}
          />
        </Box>
      </CardContent>

      <Dialog open={isEditOpen} onClose={handleEditClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Edit Customer
          <IconButton aria-label="close edit customer dialog" onClick={handleEditClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CustomerEditForm key={editDialogKey} customerId={customerId} />
        </DialogContent>
      </Dialog>
      <TaxOfficeDetailsDialog
        open={isTaxOfficeViewOpen}
        onClose={() => setIsTaxOfficeViewOpen(false)}
        taxOffice={assignedTaxOffice}
        taxOfficeId={entity?.taxOfficeId}
      />
      <Snackbar
        open={copyFeedback.open}
        autoHideDuration={2500}
        onClose={() => setCopyFeedback((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={copyFeedback.severity}
          onClose={() => setCopyFeedback((current) => ({ ...current, open: false }))}
          sx={{ width: "100%" }}
        >
          {copyFeedback.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default CustomerCardSummaryPanel;
